import { useState } from 'react';
import { NWClient } from '../../client/NWClient';
import { toast } from 'react-toastify';
import { Document } from '../../pages/document-vault/components/document-list-vault/types';
import { LibraryItem } from '../../pages/library/components/library-list/types';
import { useErrorMessage } from '../hooks/useErrorMessage';
import { showMessage } from '../../slices/show-plan-upgrading-message/showPlanUpgradingMessage';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { hideOverlay, showOverlay } from '../../slices/show-progress-overlay/showProgressOverlay';

export const useFileTypeDownload = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { getErrorMessage } = useErrorMessage();
  const [fileType, setFileType] = useState<'pdf' | 'doc'>('pdf');
  const [fileName, setFileName] = useState<string>('');
  const [document, setDocument] = useState<Document>(null as unknown as Document);
  const [libraryItem, setLibraryItem] = useState<LibraryItem>(null as unknown as LibraryItem);
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const handleCloseModal = () => {
    setModalOpen(false);
    setFileName('');
    setFileType('pdf');
    setDocument(null as unknown as Document);
    setLibraryItem(null as unknown as LibraryItem);
  };

  const downloadDocumentUploadItem = (id: number, name: string) => {
    dispatch(showOverlay());
    NWClient.downloadDocument(id, name)
      .then(() => {
        handleCloseModal();
      })
      .catch((error) => {
        toast.error(getErrorMessage(error));
      })
      .finally(() => {
        dispatch(hideOverlay());
      });
  };

  const handleDownloadContract = (id: number) => {
    dispatch(showOverlay());
    NWClient.downloadDocumentContract(id, fileName + '.' + fileType, fileType)
      .then(() => {
        handleCloseModal();
      })
      .catch((error) => {
        toast.error(getErrorMessage(error));
      })
      .finally(() => {
        dispatch(hideOverlay());
      });
  };

  const handleDownloadHTML = (
    htmlId: number,
    fileNameForDownloading: string,
    fileTypeForDownloading: string
  ) => {
    dispatch(showOverlay());
    NWClient.downloadHtmlDocument(
      htmlId,
      fileNameForDownloading + '.' + fileTypeForDownloading,
      fileTypeForDownloading
    )
      .then(() => {
        handleCloseModal();
      })
      .catch((error) => {
        toast.error(getErrorMessage(error));
      })
      .finally(() => {
        dispatch(hideOverlay());
      });
  };

  const handleTemplateDownload = (
    id: number,
    fileTypeForDownloading: string,
    fileNameForDownloading: string,
    ownTemplate?: boolean
  ) => {
    dispatch(showOverlay());

    NWClient.downloadLibraryHtmlDocument(
      id,
      fileTypeForDownloading,
      fileNameForDownloading,
      ownTemplate
    )
      .then(() => {
        handleCloseModal();
      })
      .catch((error) => {
        if (
          error.response?.data?.errors?.length > 0 &&
          error.response?.data?.errors[0] === t('messages.exceededPlanLimits')
        ) {
          dispatch(showMessage(t('messages.monthLimitReached')));
        } else {
          toast.error(getErrorMessage(error));
        }
      })
      .finally(() => {
        dispatch(hideOverlay());
      });
  };

  return {
    fileType,
    setFileType,
    fileName,
    setFileName,
    modalOpen,
    setModalOpen,
    handleCloseModal,
    handleDownloadContract,
    document,
    setDocument,
    downloadDocumentUploadItem,
    handleDownloadHTML,
    handleTemplateDownload,
    libraryItem,
    setLibraryItem,
  };
};
